import React, { useState } from 'react';
import axios from 'axios';
import { formatApiErrorDetail } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { KeyRound, Check } from 'lucide-react';

const ChangePasswordPage = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (!currentPassword || !newPassword || !confirmPassword) { setError('All fields are required'); return; }
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { withCredentials: true });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 flex items-start justify-center pt-16" data-testid="change-password-content">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#C69C6D]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <KeyRound size={24} className="text-[#C69C6D]" />
          </div>
          <h1 className="text-2xl font-bold text-[#121212]" style={{ fontFamily: 'Playfair Display, serif' }}>Change Password</h1>
          <p className="text-sm text-[#808080] mt-1">Update your account password</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-[#EBEBEB] p-8">
          {success ? (
            <div className="text-center py-6" data-testid="password-success">
              <div className="w-14 h-14 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Check size={24} className="text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#121212] mb-2">Password Changed!</h3>
              <p className="text-sm text-[#808080] mb-6">Your password has been updated successfully.</p>
              <Button onClick={() => setSuccess(false)} variant="outline" className="rounded-lg">Change Again</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="password-error">{error}</div>}
              <div>
                <Label className="mb-1.5 block text-sm font-medium">Current Password *</Label>
                <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter your current password" data-testid="current-password" />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-medium">New Password *</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 characters" data-testid="new-password" />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm font-medium">Confirm New Password *</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm your new password" data-testid="confirm-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#C69C6D] hover:bg-[#B38B5D] text-black font-semibold py-5 rounded-lg" data-testid="submit-password-change">
                {loading ? 'Updating...' : 'Change Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
